import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X, LayoutPanelTop, ShoppingBag, Smartphone, TabletSmartphone, Megaphone, Store, UtensilsCrossed, Globe } from "lucide-react";

const links = [
  { label: "Calculator", href: "/calculator" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "Pricing", href: "/pricing" },
];

const features = [
  { icon: LayoutPanelTop, title: "Website", desc: "No-code, drag & drop, unlimited pages, SEO-rich", href: "/features/website" },
  { icon: ShoppingBag, title: "Shop", desc: "Just Eat-style shop, Swisspayout & TWINT, vouchers, zone pricing", href: "/features/shop" },
  { icon: TabletSmartphone, title: "Partner App", desc: "Android PDA / tablet with printer — accept, print, control all", href: "/features/partner-app" },
  { icon: Smartphone, title: "Customer Apps", desc: "Real native iOS & Android apps — no PWA, no webviews", href: "/features/customer-apps" },
  { icon: Megaphone, title: "Marketing Tools", desc: "Vouchers, BOGO deals, newsletters & SMS — drag & drop", href: "/features/marketing-tools" },
];

const industries = [
  { icon: Store, title: "Retail Stores", desc: "Sell your products online with your own shop", href: "/features/retail-stores" },
  { icon: UtensilsCrossed, title: "Restaurants & Pizzerias", desc: "Takeaway & delivery without the commission bite", href: "/features/restaurants-pizzerias" },
  { icon: Globe, title: "Anyone Selling Online", desc: "If you sell something, this system sells it", href: "/features/sell-online" },
];

const DELIVERY_IMG = "https://images.unsplash.com/photo-1695654390723-479197a8c4a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxmb29kJTIwZGVsaXZlcnklMjBkYXJrJTIwY2luZW1hdGljfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85";
const CHEF_IMG = "https://images.unsplash.com/photo-1621494548002-bfc916172ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwY2hlZiUyMGRhcmslMjBwcmVtaXVtfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85";

export const scrollTo = (id) => {
  if (id.startsWith("/")) {
    window.location.href = id;
    return;
  }
  const el = document.querySelector(id);
  if (!el) {
    window.location.href = `/${id}`;
    return;
  }
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const navCls = "font-display font-bold text-[15px] text-ink/70 hover:text-brand transition-colors duration-300";

const PromoCard = ({ img, title, label, href, testid }) => (
  <button data-testid={testid} onClick={() => scrollTo(href)} className="group relative rounded-xl overflow-hidden h-[160px] text-left w-full">
    <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover spotlight-img transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-black/50 transition-colors duration-300 group-hover:bg-black/40" />
    <div className="absolute bottom-0 left-0 right-0 p-5">
      <div className="font-display font-bold text-white leading-snug text-lg">{title}</div>
      <div className="mt-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-white/80 group-hover:text-white transition-colors duration-300">
        {label} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
      </div>
    </div>
  </button>
);

const MenuItem = ({ item, testidPrefix, onClick }) => (
  <button
    data-testid={`${testidPrefix}-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    onClick={onClick}
    className="group flex items-start gap-3.5 text-left rounded-xl px-3 py-3 hover:bg-black/[0.04] transition-colors duration-200"
  >
    <span className="w-9 h-9 rounded-lg bg-black/[0.05] group-hover:bg-brand/10 flex items-center justify-center shrink-0 transition-colors duration-200">
      <item.icon className="w-[18px] h-[18px] text-black/55 group-hover:text-brand transition-colors duration-200" />
    </span>
    <span>
      <span className="block font-display font-bold text-[15px] text-ink group-hover:text-brand transition-colors duration-200">{item.title}</span>
      <span className="block mt-0.5 text-xs text-black/50 font-light leading-snug">{item.desc}</span>
    </span>
  </button>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setProductOpen(false);
    setMobileOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
        scrolled || productOpen ? "bg-white/90 backdrop-blur-2xl border-black/10" : "bg-white/60 backdrop-blur-md border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[70px] flex items-center justify-between relative">
        <button data-testid="logo-button" onClick={() => go("/")} className="font-display font-black text-xl tracking-tighter uppercase text-ink">
          simply<span className="text-brand">Owner</span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          <div className="static" onMouseEnter={() => setProductOpen(true)} onMouseLeave={() => setProductOpen(false)}>
            <button
              data-testid="product-menu-button"
              onClick={() => go("/features")}
              className={`flex items-center gap-1.5 ${navCls} ${productOpen ? "text-brand" : ""}`}
            >
              Product
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${productOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {productOpen && (
                <motion.div
                  data-testid="product-dropdown"
                  initial={{ opacity: 0, y: 14, scale: 0.99 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.99 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-0 right-0 top-full pt-4 flex justify-center"
                >
                  <div className="bg-white w-[min(1180px,94vw)] rounded-2xl border border-black/10 shadow-[0_40px_90px_-20px_rgba(0,0,0,0.3)] p-8 grid grid-cols-3 gap-8">
                    <div className="flex flex-col gap-1">
                      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-black/40 mb-4 px-3">The Product</div>
                      {features.map((f) => (
                        <MenuItem key={f.title} item={f} testidPrefix="product-item" onClick={() => go(f.href)} />
                      ))}
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-black/40 mb-4 px-3">Target Industries</div>
                      {industries.map((f) => (
                        <MenuItem key={f.title} item={f} testidPrefix="industry-item" onClick={() => go(f.href)} />
                      ))}
                    </div>
                    <div className="flex flex-col gap-4">
                      <div onClick={() => setProductOpen(false)}>
                        <PromoCard img={DELIVERY_IMG} title="What do the platforms really cost you?" label="Savings calculator" href="/calculator" testid="product-calculator-link" />
                      </div>
                      <div onClick={() => setProductOpen(false)}>
                        <PromoCard img={CHEF_IMG} title="See your own shop live in 30 minutes" label="Book a free demo" href="/#contact" testid="product-demo-link" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              onClick={() => go(l.href)}
              className={navCls}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-black/15 text-ink"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-2xl border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {[...features, ...industries].map((f) => (
                <button
                  key={f.title}
                  data-testid={`mobile-product-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => go(f.href)}
                  className="text-left font-display font-bold text-base text-ink/70 hover:text-brand transition-colors duration-200"
                >
                  {f.title}
                </button>
              ))}
              <div className="h-px bg-black/10" />
              {links.map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-base text-ink/70 hover:text-brand transition-colors duration-200"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
